import React, { useState, useEffect, useRef } from "react";
import DeleteModal from "../../Components/Common/DeleteModal/index";
import FormSeparator from "../../Components/Common/Form/FormSeparator";
import { Loader } from "../../Components/Loader";
import Page from "../../Components/Page/Page";
import IconButton from "../../Components/Common/Button/Icon/IconButton";
import { FileList } from "../../Components/Common/FileList/FileList";
import FormFileBrowser from "../../Components/Common/Form/FormFileBrowser/FormFileBrowser";
import { ReactComponent as PdfLogo } from "../../static/icons/pdf.svg";
import { ReactComponent as TiffLogo } from "../../static/icons/tiff.svg";
import { ReactComponent as NcLogo } from "../../static/icons/nc.svg";
import { ReactComponent as GmlLogo } from "../../static/icons/gml.svg";
import { getDownloadLink } from "./../../api/storageApi";
import { getCaseSetup } from "../../api/simulationApi";
import { postCustomTask } from "../../api/caseApi";
import { Save, Download, ChevronDown, ChevronUp, Info, AlertCircle } from "react-feather";
import { cloneDeep } from "lodash";
import * as actions from "../../redux/actions/projectFilesActions";
import { useDispatch, useSelector } from "react-redux";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./Files.scss";

const acceptedFileTypes = [
  "application/pdf",
  "image/tiff",
  "application/gml+xml",
];

const Files = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const project = useSelector((state) => state.projects?.loadedProject);
  const currentCase = useSelector((state) => state.projects?.currentCase);
  const uploadResults = useSelector(
    (state) => state.projectFiles.filesByProject[project?.id]
  );
  const isUploading = useSelector((state) => state.projectFiles.isUploading);
  const filesBeingDeleted = useSelector(
    (state) => state.projectFiles.filesBeingDeleted
  );
  const deletedFiles = useSelector((state) => state.projectFiles.deletedFiles);
  const osmNotification = useSelector((state) => state.tools.osmNotification);

  const [newFile, setNewFile] = useState(null);
  const [projectFiles, setProjectFiles] = useState([]);
  const [pdfExpanded, setPdfExpanded] = useState(true);
  const [tiffExpanded, setTiffExpanded] = useState(false);
  const [ncExpanded, setNcExpanded] = useState(false);
  const [citygmlExpanded, setCitygmlExpanded] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    show: false,
    data: null,
  });
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isError, setIsError] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState(null);
  const prevNotificationRef = useRef(null);

  const showDeleteModal = (data) => setDeleteModal({ show: true, data: data });
  const closeDeleteModal = () =>
    setDeleteModal({
      show: false,
      data: null,
    });

  const newFileChange = (file) => {
    const fileName = file.name;
    const extensionName = fileName.split(".").pop();

    if (
      acceptedFileTypes.includes(file.type) ||
      extensionName === "nc" ||
      extensionName === "gml"
    ) {
      setNewFile(file);
      return;
    }
    setNewFile(null);
  };

  useEffect(() => {
    if (isUploading) {
      return;
    }

    setProjectFiles((prevFiles) => {
      const succeededUploads =
        uploadResults?.filter(
          (r) => !prevFiles.some((p) => p.path === r.path)
        ) || [];

      const newUploads = succeededUploads.filter(
        (f) => !prevFiles.some((pf) => pf.name === f.name)
      );

      if (newUploads.length > 0) {
        if (!!newUploads.filter((f) => f.type.includes("pdf")).length > 0) {
          setPdfExpanded(true);
        }
        if (!!newUploads.filter((f) => f.type.includes("tif")).length > 0) {
          setTiffExpanded(true);
        }
        if (
          !!newUploads.filter(
            (f) => f.type.includes("nc") || f.name.endsWith(".nc")
          ).length > 0
        ) {
          setNcExpanded(true);
        }
        if (
          !!newUploads.filter(
            (f) => f.type.includes("gml") || f.name.endsWith(".gml")
          ).length > 0
        ) {
          setCitygmlExpanded(true);
        }
      }

      setNewFile(null);
      return [...succeededUploads, ...prevFiles];
    });
  }, [uploadResults, isUploading]);

  useEffect(() => {
    if (!project) {
      setProjectFiles([]);
      return;
    }

    if (project) {
      const caseDetailsDto = cloneDeep(project.caseDetailsDto);
      if (caseDetailsDto.length !== 0) {
        const ncIndexToDisable = [];
        const tiffIndexToDisable = [];

        if (caseDetailsDto.some((x) => x.rasterParameters?.additionalData)) {
          caseDetailsDto.forEach((x) => {
            const buildingSettings =
              x.rasterParameters?.additionalData?.globalParameters
                ?.buildingSettings;
            const treeSettings =
              x.rasterParameters?.additionalData?.globalParameters
                ?.treeSettings;

            if (buildingSettings?.bhParentFile) {
              const tiffIndexInProjectFiles = project.files?.findIndex(
                (y) => y.path === buildingSettings.bhParentFile.path
              );
              if (tiffIndexInProjectFiles !== -1) {
                tiffIndexToDisable.push(tiffIndexInProjectFiles);
              }
            }

            if (treeSettings?.thParentFile) {
              const tiffIndexInProjectFiles = project.files?.findIndex(
                (y) => y.path === treeSettings.thParentFile.path
              );
              if (tiffIndexInProjectFiles !== -1) {
                tiffIndexToDisable.push(tiffIndexInProjectFiles);
              }
            }

            if (x?.rasterParameters?.childRasterParameters) {
              if (buildingSettings?.bhChildFile) {
                const tiffIndexInProjectFiles = project.files?.findIndex(
                  (y) => y.path === buildingSettings.bhChildFile.path
                );
                if (tiffIndexInProjectFiles !== -1) {
                  tiffIndexToDisable.push(tiffIndexInProjectFiles);
                }
              }

              if (treeSettings?.thChildFile) {
                const tiffIndexInProjectFiles = project.files?.findIndex(
                  (y) => y.path === treeSettings.thChildFile.path
                );
                if (tiffIndexInProjectFiles !== -1) {
                  tiffIndexToDisable.push(tiffIndexInProjectFiles);
                }
              }
            }
          });
        }

        if (caseDetailsDto.some((x) => x.applicationField === 1)) {
          const getFiles = async () => {
            const getCasePromise = caseDetailsDto.map(async (x) => {
              const caseSetup = await getCaseSetup(x.id);
              const caseDynamicDriverPath =
                caseSetup.setup?.settings?.thermal_comfort_parameters
                  ?.path_user_dynamic_driver?.path;
              if (caseDynamicDriverPath) {
                const ncIndexInProjectFiles = project.files?.findIndex(
                  (x) => x.path === caseDynamicDriverPath
                );
                if (ncIndexInProjectFiles !== -1) {
                  ncIndexToDisable.push(ncIndexInProjectFiles);
                }
              }
            });

            await Promise.all(getCasePromise);
            project.files.forEach((x, i) => {
              x.disabled = false;
              if (
                (ncIndexToDisable.length !== 0 &&
                  ncIndexToDisable.includes(i)) ||
                (tiffIndexToDisable.length !== 0 &&
                  tiffIndexToDisable.includes(i))
              ) {
                x.disabled = true;
              }
            });
            setProjectFiles(project.files);
          };
          getFiles();
        } else {
          project.files.forEach((x, i) => {
            x.disabled = false;
            if (
              (ncIndexToDisable.length !== 0 && ncIndexToDisable.includes(i)) ||
              (tiffIndexToDisable.length !== 0 &&
                tiffIndexToDisable.includes(i))
            ) {
              x.disabled = true;
            }
          });
          setProjectFiles(project.files);
        }
      }
    } else {
      console.log(project.files);
      setProjectFiles(project.files?.reverse());
    }
  }, [project]);

  // Update selectAll state when selectedFiles changes
  useEffect(() => {
    const allFilePaths = projectFiles
      .filter((f) => !deletedFiles.includes(f.name) && !f.disabled)
      .map((f) => f.path);
    const allSelected =
      allFilePaths.length > 0 &&
      allFilePaths.every((path) => selectedFiles.has(path));
    setSelectAll(allSelected);
  }, [selectedFiles, projectFiles, deletedFiles]);

  // Track notification status for multiProjectFilesDownloader tasks
  useEffect(() => {
    if (osmNotification && currentRequestId) {
      const notification = osmNotification;
      const prevNotification = prevNotificationRef.current;

      // Only process notifications for multiProjectFilesDownloader tasks
      if (
        notification.type === "multiProjectFilesDownloader" &&
        notification.requestId === currentRequestId
      ) {
        // Status 2 = finished successfully
        if (notification.status === 2 && prevNotification?.status !== 2) {
          setIsProcessing(false);
          setIsError(false);
        }
        // Status 3 = error
        else if (prevNotification?.status !== 3 && notification.status === 3) {
          setIsError(true);
          setIsProcessing(false);
        }
        // Status 0 or 1 = processing (initializing or running)
        else if (
          (notification.status === 0 || notification.status === 1) &&
          !isProcessing
        ) {
          setIsProcessing(true);
          setIsError(false);
        }
      }
    }
    prevNotificationRef.current = osmNotification;
  }, [osmNotification, currentRequestId, isProcessing]);

  const saveFile = () => {
    dispatch(actions.updateFile(project.id, newFile));
  };

  const deleteFile = (file) => {
    dispatch(actions.deleteFile(project.id, file.name));
    closeDeleteModal();
  };

  const openFile = async (filePath) => {
    const url = await getDownloadLink(filePath);
    window.open(url);
  };

  const downloadFile = async (filePath) => {
    const url = await getDownloadLink(filePath);
    var link = document.createElement("a");
    link.download = "download";
    link.href = url;
    link.click();
  };

  const handleFileCheckboxChange = (file, checked) => {
    if (file.disabled) {
      return; // Don't allow selection of disabled files
    }
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(file.path);
      } else {
        newSet.delete(file.path);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allFilePaths = projectFiles
        .filter((f) => !deletedFiles.includes(f.name) && !f.disabled)
        .map((f) => f.path);
      setSelectedFiles(new Set(allFilePaths));
    } else {
      setSelectedFiles(new Set());
    }
  };

  const isFileChecked = (file) => {
    return selectedFiles.has(file.path);
  };

  const handleSectionSelectAll = (fileType, checked) => {
    const sectionFiles = projectFiles.filter((f) => {
      if (f.disabled || deletedFiles.includes(f.name)) return false;
      let fileTypeCheck = f.type;
      if (
        !fileTypeCheck ||
        fileTypeCheck === "" ||
        fileTypeCheck === "application/octet-stream"
      ) {
        const extension = f.name.split(".").pop().toLowerCase();
        if (extension === "nc") {
          fileTypeCheck = "application/netcdf";
        } else if (extension === "gml") {
          fileTypeCheck = "application/gml+xml";
        }
      }
      return fileTypeCheck === fileType;
    });
    setSelectedFiles((prev) => {
      const newSet = new Set(prev);
      if (checked) {
        sectionFiles.forEach((f) => newSet.add(f.path));
      } else {
        sectionFiles.forEach((f) => newSet.delete(f.path));
      }
      return newSet;
    });
  };

  const isSectionAllChecked = (fileType) => {
    const sectionFiles = projectFiles.filter((f) => {
      if (f.disabled || deletedFiles.includes(f.name)) return false;
      let fileTypeCheck = f.type;
      if (
        !fileTypeCheck ||
        fileTypeCheck === "" ||
        fileTypeCheck === "application/octet-stream"
      ) {
        const extension = f.name.split(".").pop().toLowerCase();
        if (extension === "nc") {
          fileTypeCheck = "application/netcdf";
        } else if (extension === "gml") {
          fileTypeCheck = "application/gml+xml";
        }
      }
      return fileTypeCheck === fileType;
    });
    if (sectionFiles.length === 0) return false;
    return sectionFiles.every((f) => selectedFiles.has(f.path));
  };

  const expandAll = () => {
    setPdfExpanded(true);
    setTiffExpanded(true);
    setNcExpanded(true);
    setCitygmlExpanded(true);
  };

  const collapseAll = () => {
    setPdfExpanded(false);
    setTiffExpanded(false);
    setNcExpanded(false);
    setCitygmlExpanded(false);
  };

  const onSaveSelected = async () => {
    const selected = Array.from(selectedFiles)
      .map((filePath) => {
        const file = projectFiles.find((f) => f.path === filePath);
        if (file) {
          return {
            name: file.name,
            url: filePath,
          };
        }
        return null;
      })
      .filter((x) => x !== null);

    if (selected.length === 0) {
      console.log("No files selected");
      return;
    }

    // Use first case from project if currentCase is not available
    const caseId = currentCase?.id || project?.caseDetailsDto?.[0]?.id;

    if (!caseId) {
      console.error("Case ID not available");
      return;
    }

    try {
      setIsProcessing(true);
      setIsError(false);
      setCurrentRequestId(null);

      const taskRequest = {
        caseId: caseId,
        type: "multiProjectFilesDownloader",
        request: {
          files: selected,
        },
      };
      console.log(taskRequest);
      const requestId = await postCustomTask(taskRequest);
      console.log("Custom task requested for project files:", requestId);

      // Store requestId to track this specific task
      setCurrentRequestId(requestId);
      // Clear selection after successful request
      //setSelectedFiles(new Set());
      //setSelectAll(false);
    } catch (error) {
      console.error("Error requesting custom task:", error);
      setIsProcessing(false);
      setIsError(true);
    }
  };

  const groupedFiles = projectFiles
    .filter((f) => !deletedFiles.includes(f.name))
    .reduce((groups, file) => {
      // Determine file type based on extension if MIME type is empty or generic
      let fileType = file.type;
      if (
        !fileType ||
        fileType === "" ||
        fileType === "application/octet-stream"
      ) {
        const extension = file.name.split(".").pop().toLowerCase();
        if (extension === "nc") {
          fileType = "application/netcdf";
        } else if (extension === "gml") {
          fileType = "application/gml+xml";
        }
      }

      (groups[fileType] = groups[fileType] || []).push({
        ...file,
        isDeleting: filesBeingDeleted.includes(file.name),
      });
      return groups;
    }, {});

  return project ? (
    <Page>
      <div className="files-page-wrapper" style={{ width: "500px" }}>
        <h2 style={{ fontSize: 28 }} className="settings-hdr">
          {t("Files")}
        </h2>
        <div style={{ width: "450px" }}>
          <FormSeparator label={t("UploadFile")} />
        </div>
        <div className="file-upload">
          <FormFileBrowser
            name="addFile"
            disabled={isUploading}
            fileName={newFile?.name || ""}
            onChange={newFileChange}
            clearUploadedFile={() => setNewFile(null)}
            accept={[".tif", ".tiff", ".pdf", ".nc", ".gml"]}
            projectFiles
            isClearable
          />
          {!!newFile && !isUploading && (
            <IconButton
              className="file-submit-btn"
              icon={Save}
              onClick={saveFile}
            />
          )}
          {isUploading && <Loader size="x-small" />}
        </div>
        <div style={{ width: "450px", marginTop: "-8px" }}>
          <div
            className="file-upload-description"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Info size={"14px"} strokeWidth={"1.6px"} />
            {t("PdfUploadDescription")}
          </div>
          <div
            className="file-upload-description"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Info size={"14px"} />
            {t("GeotiffUploadDescription")}
          </div>
          {/* <div
            className="file-upload-description"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Info size={"14px"} />
            {t("GeotiffDgmUploadDescription")}
          </div> */}
          <div
            className="file-upload-description"
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Info size={"14px"} />
            {t("NetcdfUploadDescription")}
          </div>
        </div>
        <div style={{ width: "450px" }}>
          <FormSeparator label={t("UploadedFilesList")} />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            width: "450px",
            marginBottom: "12px",
          }}
        >
          <div style={{ display: "flex", marginRight: "12px" }}>
            <input
              type="checkbox"
              style={{ width: "12px" }}
              checked={selectAll}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            <span
              style={{
                fontSize: "12px",
                marginLeft: "4px",
                alignContent: "center",
              }}
            >
              {t("SelectAll")}
            </span>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "7px",
            }}
          >
            <button
              className="btn-modal-cancel"
              onClick={expandAll}
              style={{
                display: "flex",
                alignItems: "center",
                color: "#444",
                padding: "1px 5px",
                minWidth: "auto",
                justifyContent: "center",
                border: "1px solid #a7a7a7",
                marginRight: "0px"
              }}
              title={t("ExpandAll")}
            >
              <ChevronDown size={"12px"} strokeWidth={"1.8px"} />
            </button>
            <button
              className="btn-modal-cancel"
              onClick={collapseAll}
              style={{
                display: "flex",
                alignItems: "center",
                color: "#444",
                padding: "1px 5px",
                minWidth: "auto",
                justifyContent: "center",
                border: "1px solid #a7a7a7",
              }}
              title={t("CollapseAll")}
            >
              <ChevronUp size={"12px"} strokeWidth={"1.8px"} />
            </button>
          </div>
        </div>
        <FileList
          items={(groupedFiles["application/pdf"] || []).map((f) => ({
            ...f,
            icon: PdfLogo,
          }))}
          label={t("Pdf")}
          expanded={pdfExpanded}
          onDelete={(f) => showDeleteModal(f)}
          viewable
          onView={(f) => openFile(f.path)}
          onDownload={(f) => downloadFile(f.path)}
          showCheckbox={true}
          isFileChecked={isFileChecked}
          onCheckboxChange={(e) => {
            const filePath = e.target.getAttribute("data-file-path");
            const file = projectFiles.find((f) => f.path === filePath);
            if (file) {
              handleFileCheckboxChange(file, e.target.checked);
            }
          }}
          showHeaderCheckbox={true}
          headerCheckboxChecked={isSectionAllChecked("application/pdf")}
          onHeaderCheckboxChange={(checked) =>
            handleSectionSelectAll("application/pdf", checked)
          }
        />
        <FileList
          className="file-list__nc"
          items={(groupedFiles["image/tiff"] || []).map((f) => ({
            ...f,
            icon: TiffLogo,
          }))}
          label={t("Tiff")}
          expanded={tiffExpanded}
          isDeleting={true}
          onDelete={(f) => showDeleteModal(f)}
          onDownload={(f) => downloadFile(f.path)}
          showCheckbox={true}
          isFileChecked={isFileChecked}
          onCheckboxChange={(e) => {
            const filePath = e.target.getAttribute("data-file-path");
            const file = projectFiles.find((f) => f.path === filePath);
            if (file) {
              handleFileCheckboxChange(file, e.target.checked);
            }
          }}
          showHeaderCheckbox={true}
          headerCheckboxChecked={isSectionAllChecked("image/tiff")}
          onHeaderCheckboxChange={(checked) =>
            handleSectionSelectAll("image/tiff", checked)
          }
        />
        <FileList
          className="file-list__nc"
          items={(groupedFiles["application/netcdf"] || []).map((f) => ({
            ...f,
            icon: NcLogo,
          }))}
          label={t("NC")}
          expanded={ncExpanded}
          isDeleting={true}
          onDelete={(f) => showDeleteModal(f)}
          onDownload={(f) => downloadFile(f.path)}
          showCheckbox={true}
          isFileChecked={isFileChecked}
          onCheckboxChange={(e) => {
            const filePath = e.target.getAttribute("data-file-path");
            const file = projectFiles.find((f) => f.path === filePath);
            if (file) {
              handleFileCheckboxChange(file, e.target.checked);
            }
          }}
          showHeaderCheckbox={true}
          headerCheckboxChecked={isSectionAllChecked("application/netcdf")}
          onHeaderCheckboxChange={(checked) =>
            handleSectionSelectAll("application/netcdf", checked)
          }
        />
        {/* <FileList
          className="file-list__citygml"
          items={(groupedFiles["application/gml+xml"] || []).map((f) => ({
            ...f,
            icon: GmlLogo,
          }))}
          label={t("CityGML")}
          expanded={citygmlExpanded}
          isDeleting={true}
          onDelete={(f) => showDeleteModal(f)}
          onDownload={(f) => downloadFile(f.path)}
          showCheckbox={true}
          isFileChecked={isFileChecked}
          onCheckboxChange={(e) => {
            const filePath = e.target.getAttribute("data-file-path");
            const file = projectFiles.find((f) => f.path === filePath);
            if (file) {
              handleFileCheckboxChange(file, e.target.checked);
            }
          }}
          showHeaderCheckbox={true}
          headerCheckboxChecked={isSectionAllChecked("application/gml+xml")}
          onHeaderCheckboxChange={(checked) =>
            handleSectionSelectAll("application/gml+xml", checked)
          }
        /> */}
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            flexDirection: "column",
            width: "450px",
          }}
        >
          {isProcessing && (
            <div>
              <div
                style={{
                  display: "flex",
                  marginTop: -25,
                  justifyContent: "center"
                }}
              >
                <Loader size="small-osm" style={{ marginLeft: "0px" }} />
              </div>
              <p style={{ fontSize: 11, cursor: "default", color: "gray", textAlign: "center" }}>
                {t("Processing")}...
              </p>
            </div>
          )}

          {!isProcessing && isError && (
            <div
              style={{
                display:"flex",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <AlertCircle size="12px" color="#ac4141" />
              <p
                className="new-layer-name-error"
                style={{
                  fontSize: "12px",
                }}
              >
                {t("ErrorProcessingFiles") ||
                  "An error occurred while processing files. Please try again."}
              </p>
            </div>
          )}

          <button
            className="btn-modal-cancel"
            onClick={onSaveSelected}
            disabled={selectedFiles.size === 0 || isProcessing}
            style={{
              display: "flex",
              alignItems: "center",
              color: "#444",
              padding: "5px 6px",
              minWidth: "110px",
              width: "auto",
              maxWidth: "none",
              alignSelf: "flex-end",
              justifyContent: "space-around",
              marginTop: isProcessing ? "-40px" : "0px",
              opacity: selectedFiles.size === 0 || isProcessing ? 0.5 : 1,
              cursor:
                selectedFiles.size === 0 || isProcessing
                  ? "not-allowed"
                  : "pointer",
            }}
          >
            <div style={{ display: "flex" }}>
              <Download size={"14px"} strokeWidth={"1.8px"} />
            </div>
            {t("SaveSelected")}
          </button>
        </div>
      </div>
      <DeleteModal
        modalHdr={t("DeleteFile")}
        modalInfo={t("DeleteFileModalInfo")}
        isOpen={deleteModal.show}
        closeDeleteModal={closeDeleteModal}
        deleteModalData={() =>
          deleteModal.data ? deleteFile(deleteModal.data) : null
        }
      />
    </Page>
  ) : (
    <Loader />
  );
};

export default withRouter(Files);
